import { useEffect, useRef, useState } from "react";
import GetUserInfoService from "../service/GetUserInfoService";
import useApiStatus from "./useApiStatus";
import GetInfoService from "../service/GetInfoService";
import { paypalCurrency } from "../utilities/paypal";
import { useHistory } from "react-router-dom";
import { formatDate } from "../utilities/formatDate";
import AdvancedPayPalService from "../service/AdvancedPayPalService";

const useRenewSubscription = ({
  currency,
  setPopUpNotVisible,
  setSubscriptionInfo,
  onSuccess,
  onError,
}) => {
  const {
    isLoading: isLoadingRenewSubscriptionAmoutToPay,
    setIsLoading: setIsLoadingRenewSubscriptionAmoutToPay,
    setHasError,
  } = useApiStatus({ isLoading: true });
  const [renewSubscriptionAmoutToPay, setRenewSubscriptionAmoutToPay] = useState();
  const [freeRenewalsRemaining, setFreeRenewalsRemaining] = useState();

  const orderId = useRef(null);

  const {
    isLoading: isLoadingRenewSubscription,
    setIsLoading: setIsLoadingRenewSubscription,
    setHasError: setHasErrorRenewSubscription,
  } = useApiStatus({ isLoading: false });

  const Navigate = useHistory();

  const advancedPayPalService = new AdvancedPayPalService();
  const getUserInfoService = new GetUserInfoService();
  const getInfoService = new GetInfoService();

  const checkAmoutSubscription = () => {
    getUserInfoService
      .getSubscriptionAmoutToPay(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
      )
      .then((res) => {
        if (res.messageCode !== 200001) {
          setHasError(true);
          onError({ code: res.messageCode });
        } else {
          if (res.result.currency !== paypalCurrency) {
            getInfoService
              .getForexRate(res.result.currency, paypalCurrency)
              .then((paypal) => {
                if (res.messageCode !== 200001) {
                  setHasError(true);
                  onError({ code: paypal.messageCode });
                } else {
                  setHasError(false);
                  setRenewSubscriptionAmoutToPay({
                    ...res.result,
                    paypalValue: parseFloat((res.result.amountDue * paypal.result).toFixed(2)),
                  });
                }
              })
              .catch((error) => {
                console.log(error);
                setHasError(true);
                setIsLoadingRenewSubscriptionAmoutToPay(false);
              })
              .finally(() => {
                setIsLoadingRenewSubscriptionAmoutToPay(false);
              });
          } else {
            setHasError(false);
            setRenewSubscriptionAmoutToPay({ ...res.result, paypalValue: res.result.amountDue });
            setIsLoadingRenewSubscriptionAmoutToPay(false);
          }
        }
      })
      .catch((error) => {
        console.log(error);
        setHasError(true);
        setIsLoadingRenewSubscriptionAmoutToPay(false);
      });
  };

  const handleRenewSubscription = (methodId, transactionId = undefined) => {
    // methodId: 1 = paypal | 4 = wallet
    setIsLoadingRenewSubscription(true);

    getUserInfoService
      .postSubscriptionRenew(
        localStorage.getItem("__extraconomy_username"),
        localStorage.getItem("__extraconomy_token"),
        methodId,
        transactionId,
      )
      .then((res) => {
        if (res.messageCode !== 200000) {
          setHasErrorRenewSubscription(true);

          if (!freeRenewalsRemaining) setPopUpNotVisible();

          onError({ code: res.messageCode });
        } else {
          if (!freeRenewalsRemaining) setPopUpNotVisible();

          setHasErrorRenewSubscription(false);

          setSubscriptionInfo({
            ...res.result,
            subscriptionFormattedData: `${formatDate(new Date(res.result.dateBegin))} -
          ${formatDate(new Date(res.result.dateEnd))}`,
            subscriptionDaysLeft: ((new Date(res.result.dateEnd) - Date.now()) / 86400000) | 0,
          });

          Navigate.push("/dashboard");

          onSuccess();
        }
      })
      .catch((error) => {
        console.log(error);
        setHasErrorRenewSubscription(true);
      })
      .finally(() => {
        setIsLoadingRenewSubscription(false);
      });
  };

  const checkFreeRenewalsRemaining = async () => {
    try {
      const response = await getUserInfoService.getFreeRenewalsRemaining(
        localStorage.getItem("__extraconomy_username"),
        localStorage.getItem("__extraconomy_token"),
      );

      const data = await response.json();

      if (data === null) {
        setFreeRenewalsRemaining(0);
        return 0;
      } else {
        setFreeRenewalsRemaining(data.remainingRenewals);
        return data.remainingRenewals;
      }
    } catch (error) {
      setFreeRenewalsRemaining(0);
      return 0;
    }
  };

  const createOrder = async () => {
    const payload = {
      amount: renewSubscriptionAmoutToPay.paypalValue,
      currency: "EUR",
      returnUrl: "https://example.com/success",
      cancelUrl: "https://example.com/cancel",
    };
    try {
      const res = await advancedPayPalService.createOrder(payload);
      orderId.current = res.id;
      return res.id;
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    checkAmoutSubscription();
    checkFreeRenewalsRemaining(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  return {
    renewSubscriptionAmoutToPay,
    isLoadingRenewSubscription,
    isLoadingRenewSubscriptionAmoutToPay,
    freeRenewalsRemaining,
    checkAmoutSubscription,
    handleRenewSubscription,
    createOrder,
  };
};

export default useRenewSubscription;
